import type { Monaco } from "@monaco-editor/react";
import type { FormComponentPropLanguages } from "components/Form/components/FormComponentProps";
import { buildWorkerDefinition } from "monaco-editor-workers";

/**
 * Monaco is loaded on demand in two profiles:
 *
 * - "light" (yaml, text): editor.api.js + core commands + basic-languages yaml tokenizer.
 *   No LSP, no editor.all.js, no vscode service overrides.
 *
 * - "python": editor.all.js + vscode service overrides + Pyright LSP. Required for
 *   monaco-vscode-api; cannot be trimmed without breaking language services.
 *
 * CodeEditor components are also React.lazy()-loaded, so neither profile is fetched until
 * a code field is actually rendered — and the python profile only when lang === "python".
 *
 * When a page has both yaml and python editors, the python LSP bootstrap must finish
 * before ANY editor mounts. Otherwise a light editor calls StandaloneServices.initialize({})
 * first and vscode service overrides can no longer be applied.
 */
type MonacoLoadProfile = "light" | "python";

const monacoPromises = new Map<MonacoLoadProfile, Promise<Monaco>>();

let workersConfigured = false;
let pythonLspRequired = false;
let bootstrapPromise: Promise<void> | null = null;

function ensureWorkersConfigured() {
    if (workersConfigured) {
        return;
    }

    buildWorkerDefinition("/workers/", new URL("", window.location.href).href, false);
    workersConfigured = true;
}

async function loadPythonMonacoInternal(): Promise<Monaco> {
    // Import order matters for monaco-vscode-api / LSP — do not reorder.
    await import("monaco-editor/esm/vs/editor/editor.all.js");
    const monaco = await import("monaco-editor/esm/vs/editor/editor.api.js");
    await import("vscode/default-extensions/json");
    await import("vscode/default-extensions/python");
    await import("vscode/default-extensions/yaml");
    await import("vscode/default-extensions/theme-defaults");
    await import("monaco-editor/esm/vs/basic-languages/python/python.contribution.js");
    await import("vscode");

    const { ensureServicesInitialized } = await import(
        "components/Form/components/CodeEditor/functionality/lspClient"
    );
    await ensureServicesInitialized();

    return monaco as unknown as Monaco;
}

/**
 * Register a lightweight Monarch tokenizer for JSON. Monaco ships JSON only as a
 * full language *service* (monaco-editor/esm/vs/language/json), which spawns a
 * jsonWorker we do not bundle in the light profile — so we register a small
 * main-thread grammar instead. Highlighting only; no validation/formatting.
 * (operator-task-fe-file-mimetype-adaptive-display.) Failure is non-fatal: the
 * editor falls back to plaintext for JSON.
 */
function registerJsonLanguage(monaco: Monaco) {
    try {
        const existing = monaco.languages.getLanguages?.() ?? [];
        if (existing.some((l: { id: string }) => l.id === "json")) {
            return;
        }
        monaco.languages.register({ id: "json", extensions: [".json"], aliases: ["JSON", "json"] });
        monaco.languages.setLanguageConfiguration("json", {
            brackets: [
                ["{", "}"],
                ["[", "]"],
            ],
            autoClosingPairs: [
                { open: "{", close: "}" },
                { open: "[", close: "]" },
                { open: '"', close: '"' },
            ],
        });
        monaco.languages.setMonarchTokensProvider("json", {
            tokenizer: {
                root: [
                    [/"(?:[^"\\]|\\.)*"\s*(?=:)/, "type"],
                    [/"(?:[^"\\]|\\.)*"/, "string"],
                    [/\b(?:true|false)\b/, "keyword"],
                    [/\bnull\b/, "keyword"],
                    [/-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/, "number"],
                    [/[{}[\],:]/, "delimiter"],
                ],
            },
        });
    } catch (e) {
        console.error("Failed to register light JSON grammar", e);
    }
}

async function loadLightMonaco(): Promise<Monaco> {
    const monaco = await import("monaco-editor/esm/vs/editor/editor.api.js");

    await import("monaco-editor/esm/vs/editor/browser/coreCommands.js");
    await import("monaco-editor/esm/vs/basic-languages/yaml/yaml.contribution.js");
    await import("monaco-editor/esm/vs/basic-languages/markdown/markdown.contribution.js");
    // Read-mostly grammars for adaptive FILE content display
    // (operator-task-fe-file-mimetype-adaptive-display). basic-languages are
    // main-thread Monarch tokenizers -> no language-service worker required.
    await import("monaco-editor/esm/vs/basic-languages/javascript/javascript.contribution.js");
    await import("monaco-editor/esm/vs/basic-languages/typescript/typescript.contribution.js");
    await import("monaco-editor/esm/vs/basic-languages/sql/sql.contribution.js");
    await import("monaco-editor/esm/vs/basic-languages/xml/xml.contribution.js");
    await import("monaco-editor/esm/vs/basic-languages/html/html.contribution.js");
    await import("monaco-editor/esm/vs/basic-languages/shell/shell.contribution.js");
    await import("monaco-editor/esm/vs/basic-languages/dockerfile/dockerfile.contribution.js");
    await import("monaco-editor/esm/vs/basic-languages/ini/ini.contribution.js");

    registerJsonLanguage(monaco as unknown as Monaco);

    return monaco as unknown as Monaco;
}

function ensurePythonMonacoPromise(): Promise<Monaco> {
    if (!monacoPromises.has("python")) {
        monacoPromises.set("python", loadPythonMonacoInternal());
    }

    return monacoPromises.get("python")!;
}

async function runBootstrap(): Promise<void> {
    ensureWorkersConfigured();

    if (pythonLspRequired) {
        await ensurePythonMonacoPromise();
    }
}

/** Call from useLayoutEffect so python need is known before any editor mounts. */
export const registerCodeEditorLanguage = (lang: FormComponentPropLanguages) => {
    if (lang === "python") {
        pythonLspRequired = true;
    }
};

/** Wait for coordinated bootstrap before loading Monaco or mounting editors. */
export const ensureMonacoBootstrap = (): Promise<void> => {
    if (!bootstrapPromise) {
        bootstrapPromise = runBootstrap();
    }

    return bootstrapPromise;
};

async function loadMonacoInternal(lang: FormComponentPropLanguages): Promise<Monaco> {
    await ensureMonacoBootstrap();

    // Re-check pythonLspRequired after bootstrap: a python editor may have registered
    // (via useLayoutEffect) after bootstrap started but before it resolved. Routing all
    // editors through the python profile ensures vscode service overrides are applied
    // before any editor calls monaco.editor.create() → StandaloneServices.initialize({}).
    if (lang === "python" || pythonLspRequired) {
        return ensurePythonMonacoPromise();
    }

    if (!monacoPromises.has("light")) {
        monacoPromises.set("light", loadLightMonaco());
    }

    return monacoPromises.get("light")!;
}

export const loadMonaco = (lang: FormComponentPropLanguages = "text"): Promise<Monaco> => {
    return loadMonacoInternal(lang);
};

export const getEditorTheme = (lang: FormComponentPropLanguages | undefined) =>
    lang === "python" ? "Default Light+" : "vs";
